/*
 * Agency defaults (QA doc docs features/website/currency-and-language.md R-CAL17 .. R-CAL19; Amiran 27.10.2026: "for russian agencies we should put
 * rub as a default curency"): after each sign-in the agency's default currency is read once from online-api `GET Catalogue/GetAgencyDefaults`
 * (RUB for an agency whose contact city is in Russia, null otherwise) and handed to the store (`appSetAgencyCurrency`), which opens the header on it
 * when the destination converts to it. A failed read = no agency default (the destination's sell currency applies); nothing is shown for it.
 */
import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { appSetAgencyCurrency } from '@app-state/app.actions';
import { AuthState } from '@core/modules/auth/state/auth.state';
import { environment } from '@env/environment';
import { Store, select } from '@ngxs/store';
import { Subscription, catchError, distinctUntilChanged, filter, of, switchMap } from 'rxjs';

/** online-api's answer (R-CAL17). */
export interface AgencyDefaultsAnswer {
  currency?: string | null;
}

/** Reads the signed-in agency's defaults once per sign-in (started once by `StartupService`). */
@Injectable({ providedIn: 'root' })
export class AgencyDefaultsService {
  readonly #http = inject(HttpClient);
  readonly #injector = inject(Injector);
  readonly #store = inject(Store);
  readonly #isAuthenticated = select(AuthState.isAuthenticated);
  readonly #url = environment.urls.api + '/Catalogue/GetAgencyDefaults';
  #subscription: Subscription | null = null;

  public init(): void {
    if (this.#subscription) return;
    this.#subscription = toObservable(this.#isAuthenticated, { injector: this.#injector })
      .pipe(
        distinctUntilChanged(),
        filter((signedIn) => !!signedIn),
        switchMap(() =>
          this.#http.get<AgencyDefaultsAnswer>(this.#url).pipe(catchError(() => of<AgencyDefaultsAnswer | null>(null)))
        )
      )
      .subscribe((answer) => this.#store.dispatch(new appSetAgencyCurrency(answer?.currency ?? null)));
  }
}
